import React, { useState, useContext, useRef, useEffect } from "react";
import { graphql, navigate } from "gatsby";
import { SiteSearchResults } from "./SiteSearchResults";
import {
  KontentComponentSettings,
  KontentMultiChoice,
  StringValue,
} from "../../../types";
import { getMultiChoice } from "../../../lib/multichoice";
import { PageMetadataContext } from "../../../lib/PageMetadataContext";

interface Props extends KontentComponentSettings {
  include_content_category_filter: KontentMultiChoice<"yes" | "no">;
  content_category_filter_name: StringValue;
  include_relevancy_tags_filter: KontentMultiChoice<"yes" | "no">;
  relevancy_tags_filter_name: StringValue;
  include_page_type_filter: KontentMultiChoice<"yes" | "no">;
  page_type_filter_name: StringValue;
}

const SiteSearchResultsContainer: React.FC<Props> = (props) => {
  const location = useContext(PageMetadataContext).location;

  const searchTerm = location?.state?.data?.searchText;

  const queryParams = new URLSearchParams(location?.search);
  const intentParam = queryParams.get("i");
  const categoryParam = queryParams.get("c")?.split(",");
  const searchParam =
    intentParam === "tag"
      ? queryParams.get("q")?.split("-").join(" ")
      : queryParams.get("q");

  const createURL = (searchState: any) => {
    let newParams = [];

    if (searchState.query) {
      newParams.push(`q=${encodeURIComponent(searchState.query)}`);
    }

    if (searchState.refinementList?.contentCategory?.length) {
      newParams.push(
        `c=${searchState.refinementList?.contentCategory?.map(
          encodeURIComponent
        )}`
      );
    }

    return newParams.length ? `?${newParams.join("&")}` : "";
  };

  const searchStateToUrl = (searchState: any) =>
    searchState?.query || searchState?.refinementList?.contentCategory.length
      ? createURL(searchState)
      : "";

  const [query, setQuery] = useState({
    query: searchParam || searchTerm,
    refinementList: { contentCategory: categoryParam || [] },
  });

  const DEBOUNCE_URL_SYNC = 400;
  const debouncedSetStateRef = useRef<any>(null);

  const onSearchTermChange = (updatedSearchState: any) => {
    clearTimeout(debouncedSetStateRef.current);

    debouncedSetStateRef.current = setTimeout(
      () => navigate(searchStateToUrl(updatedSearchState)),
      DEBOUNCE_URL_SYNC
    );

    setQuery(updatedSearchState);
  };

  useEffect(() => {
    debouncedSetStateRef.current = setTimeout(
      () => navigate(searchStateToUrl(searchParam || searchTerm)),
      DEBOUNCE_URL_SYNC
    );

    return clearTimeout(debouncedSetStateRef.current);
  }, []);

  return (
    <SiteSearchResults
      id={props.id}
      hideOnMobile={
        getMultiChoice(props.component_settings__hide_on_mobile) === "yes"
      }
      anchor={props.component_settings__anchor_name.value}
      searchTerm={query}
      onSearchTermChange={onSearchTermChange}
      includeContentCategory={
        getMultiChoice(props.include_content_category_filter) === "yes"
      }
      contentCategoryTitle={props.content_category_filter_name.value}
      includePageType={getMultiChoice(props.include_page_type_filter) === "yes"}
      pageTypeTitle={props.page_type_filter_name.value}
      includeRelevancyTags={
        getMultiChoice(props.include_relevancy_tags_filter) === "yes"
      }
      relevancyTagsTitle={props.relevancy_tags_filter_name.value}
    />
  );
};

export default SiteSearchResultsContainer;

export const fragmentSiteSearchResultsContainer = graphql`
  fragment SiteSearchResults on kontent_item_component___site_search_results {
    system {
      id
      codename
    }
    internal {
      type
    }
    elements {
      include_content_category_filter {
        value {
          codename
        }
      }
      content_category_filter_name {
        value
      }
      include_page_type_filter {
        value {
          codename
        }
      }
      page_type_filter_name {
        value
      }
      include_relevancy_tags_filter {
        value {
          codename
        }
      }
      relevancy_tags_filter_name {
        value
      }
      component_settings__anchor_name {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
    }
  }
`;
